import React from 'react';
import { SelectedLocation } from '../../types';
import {
  occupations,
  languages as languageOptions,
  cleanlinessOptions,
  socialLevelOptions,
  workScheduleOptions,
  petOptions
} from '../../constants/formData';

export interface ProfileData {
  bio?: string;
  location: SelectedLocation[];
  budget: string;
  housingSituation: string;
  moveInDate: string;
  occupation: string;
  languages: string[];
  cleanliness: string;
  socialLevel: string;
  workSchedule: string;
  hasPets: string[];
  okWithPets: string[];
}

interface ProfileInfoSectionsProps {
  profileData: ProfileData | null;
}

const ProfileInfoSections: React.FC<ProfileInfoSectionsProps> = ({ profileData }) => {
  if (!profileData) {
    return (
      <div className="text-center py-8">
        <p className="text-coffee/70">No profile data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* About Me */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          📝 About Me
        </h3>
        {profileData.bio && profileData.bio.trim() ? (
          <p className="text-lg text-coffee font-bold whitespace-pre-wrap">{profileData.bio}</p>
        ) : (
          <p className="text-lg text-coffee/60 italic">No bio added yet</p>
        )}
      </div>

      <hr className="border-b-2 border-coffee/10" />

      {/* Housing Preferences */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          🏠 Housing Preferences
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-coffee/60 font-bold uppercase tracking-wider mb-1 block">Selected Areas</label>
            <div className="flex flex-wrap gap-2">
              {profileData.location && profileData.location.length > 0 ? (
                profileData.location.map((location) => (
                  <span key={location.id || `${location.borough}-${location.neighborhood}`} className="bg-transparent border-2 border-coffee text-coffee px-4 py-1 rounded-full text-sm font-bold inline-block mr-2 mb-2">
                    {location.borough} - {location.neighborhood}
                  </span>
                ))
              ) : (
                <p className="text-lg text-coffee font-bold">Not specified</p>
              )}
            </div>
          </div>
          <div>
            <label className="text-sm text-coffee/60 font-bold uppercase tracking-wider mb-1 block">Budget</label>
            <p className="text-lg text-coffee font-bold">{profileData.budget}/month</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-sm text-coffee/60 font-bold uppercase tracking-wider mb-1 block">Housing Situation</label>
            <p className="text-lg text-coffee font-bold">
              {profileData.housingSituation === 'looking' && 'Looking for a place together'}
              {profileData.housingSituation === 'have-apartment' && 'I have a place, need roommate'}
              {profileData.housingSituation === 'flexible' && 'Flexible with either option'}
              {!profileData.housingSituation && 'Not specified'}
            </p>
          </div>
          <div>
            <label className="text-sm text-coffee/60 font-bold uppercase tracking-wider mb-1 block">Move-in Month</label>
            <p className="text-lg text-coffee font-bold">
              {profileData.moveInDate || 'Not specified'}
            </p>
          </div>
        </div>
      </div>

      <hr className="border-b-2 border-coffee/10" />

      {/* Occupation */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          💼 Occupation
        </h3>
        <p className="text-lg text-coffee font-bold">
          {occupations.find(o => o.value === profileData.occupation)?.label || profileData.occupation || 'Not specified'}
        </p>
      </div>

      {/* Languages */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          🌐 Languages
        </h3>
        <div className="flex flex-wrap gap-2">
          {profileData.languages.length > 0 ? (
            profileData.languages.map((lang) => (
              <span key={lang} className="bg-transparent border-2 border-coffee text-coffee px-4 py-1 rounded-full text-sm font-bold inline-block mr-2 mb-2">
                {languageOptions.find(l => l.value === lang)?.label || lang}
              </span>
            ))
          ) : (
            <p className="text-lg text-coffee font-bold">Not specified</p>
          )}
        </div>
      </div>

      {/* Cleanliness */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          🧹 Cleanliness
        </h3>
        <p className="text-lg text-coffee font-bold">
          {cleanlinessOptions.find(o => o.value === profileData.cleanliness)?.label || 'Not specified'}
        </p>
      </div>

      {/* Social Level */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          👥 Social Level
        </h3>
        <p className="text-lg text-coffee font-bold">
          {socialLevelOptions.find(o => o.value === profileData.socialLevel)?.label || 'Not specified'}
        </p>
      </div>

      {/* Work Schedule */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          ⏰ Work Schedule
        </h3>
        <p className="text-lg text-coffee font-bold">
          {workScheduleOptions.find(o => o.value === profileData.workSchedule)?.label || 'Not specified'}
        </p>
      </div>

      {/* Has Pets */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          🐕 Has Pets
        </h3>
        <div className="flex flex-wrap gap-2">
          {profileData.hasPets.length > 0 ? (
            profileData.hasPets.map((pet) => (
              <span key={pet} className="bg-transparent border-2 border-coffee text-coffee px-4 py-1 rounded-full text-sm font-bold inline-block mr-2 mb-2">
                {petOptions.find(p => p.value === pet)?.label || pet}
              </span>
            ))
          ) : (
            <p className="text-lg text-coffee font-bold">Not specified</p>
          )}
        </div>
      </div>

      {/* OK with Pets */}
      <div>
        <h3 className="text-2xl font-bold text-coffee mb-6 border-b-2 border-coffee/10 pb-2 flex items-center gap-2">
          🐾 OK with Pets
        </h3>
        <div className="flex flex-wrap gap-2">
          {profileData.okWithPets.length > 0 ? (
            profileData.okWithPets.map((pet) => (
              <span key={pet} className="bg-transparent border-2 border-coffee text-coffee px-4 py-1 rounded-full text-sm font-bold inline-block mr-2 mb-2">
                {petOptions.find(p => p.value === pet)?.label || pet}
              </span>
            ))
          ) : (
            <p className="text-lg text-coffee font-bold">Not specified</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileInfoSections;
